import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../supabaseClient';
import { usePermissions } from '../../utils/permissions';
import {
  Box,
  Container,
  Typography,
  Button,
  TextField,
  IconButton,
  CircularProgress,
  Alert,
  Chip,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Tooltip,
  Fab,
  Card,
  CardContent,
  useTheme,
  alpha,
  Fade,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Receipt as ReceiptIcon,
  LocalShipping as LocalShippingIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Schedule as ScheduleIcon,
  Cancel as CancelIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  Delete as DeleteIcon,
  RequestQuote as RequestQuoteIcon,
  LocalShipping as LocalShippingFilledIcon
} from '@mui/icons-material';

const ProformaInvoicesPage = () => {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const { 
    hasPermission,
    canViewDeliveryDocuments, 
    canViewInventoryUsage, 
    canViewInvoicePayment, 
    canViewDeliveryDetails, 
    canViewFinalApproval 
  } = usePermissions();
  
  // State
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [filteredPOs, setFilteredPOs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [companyName, setCompanyName] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [poToDelete, setPoToDelete] = useState(null);

  // Fetch data
  useEffect(() => {
    fetchPurchaseOrders();
    fetchCompanyData();
  }, [companyId]);

  // Filter data
  useEffect(() => {
    filterPurchaseOrders();
  }, [purchaseOrders, searchTerm, filterStatus, filterPriority]);

  const fetchPurchaseOrders = async () => {
    setLoading(true);
    setError(null);
    try {
      // First, fetch all purchase orders
      const { data: orders, error: ordersError } = await supabase
        .from('customer_purchase_orders')
        .select(`
          *,
          customer:customer_id(name, company)
        `)
        .eq('company_id', companyId)
        .order('created_at', { ascending: false });

      if (ordersError) throw ordersError;

      // Then, fetch the latest po_creation record for each purchase order
      const orderIds = (orders || []).map(po => po.id);
      
      if (orderIds.length > 0) {
        const { data: poCreations, error: poCreationsError } = await supabase
          .from('po_creation')
          .select('purchase_order_id, amount_due, total_amount, created_at')
          .in('purchase_order_id', orderIds)
          .order('created_at', { ascending: false });

        if (poCreationsError) throw poCreationsError;

        // Fetch actual delivery dates from po_delivery_confirmations
        const { data: deliveryConfirmations, error: deliveryError } = await supabase
          .from('po_delivery_confirmations')
          .select('purchase_order_id, actual_delivery_date')
          .in('purchase_order_id', orderIds);

        if (deliveryError) throw deliveryError;

        // Create a map of purchase_order_id to latest po_creation
        const poCreationMap = {};
        (poCreations || []).forEach(poCreation => {
          // Only keep the first (latest) record for each purchase_order_id
          if (!poCreationMap[poCreation.purchase_order_id]) {
            poCreationMap[poCreation.purchase_order_id] = poCreation;
          }
        });

        // Create a map of purchase_order_id to actual_delivery_date
        const deliveryDateMap = {};
        (deliveryConfirmations || []).forEach(delivery => {
          if (delivery.actual_delivery_date) {
            deliveryDateMap[delivery.purchase_order_id] = delivery.actual_delivery_date;
          }
        });

        // Map the po_creation data and actual_delivery_date to each purchase order
        const enrichedOrders = (orders || []).map(po => ({
          ...po,
          latest_po_creation: poCreationMap[po.id] || null,
          actual_delivery_date: deliveryDateMap[po.id] || null
        }));

        setPurchaseOrders(enrichedOrders);
      } else {
        setPurchaseOrders([]);
      }
    } catch (err) {
      console.error('Error fetching purchase orders:', err);
      setError('Failed to load purchase orders. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanyData = async () => {
    try {
      const { data, error } = await supabase
        .from('companies')
        .select('name')
        .eq('id', companyId)
        .single();
      
      if (error) throw error;
      setCompanyName(data?.name || 'Company');
    } catch (err) {
      console.error('Error fetching company data:', err);
      setCompanyName('Company Name');
    }
  };

  const filterPurchaseOrders = () => {
    let filtered = purchaseOrders;

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(po =>
        po.po_number?.toLowerCase().includes(term) ||
        po.customer_name?.toLowerCase().includes(term) ||
        po.customer_company?.toLowerCase().includes(term) ||
        po.customer_po_number?.toLowerCase().includes(term)
      );
    }

    // Status filter
    if (filterStatus !== 'all') {
      filtered = filtered.filter(po => po.status === filterStatus);
    }

    // Priority filter
    if (filterPriority !== 'all') {
      filtered = filtered.filter(po => po.priority_level === filterPriority);
    }

    setFilteredPOs(filtered);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleCreateNew = () => {
    navigate(`/company/${companyId}/proforma-invoices/new`);
  };

  const handleViewPO = (poId) => {
    navigate(`/company/${companyId}/proforma-invoices/${poId}`);
  };

  const handleConfirmDelivery = (poId, event) => {
    event.stopPropagation(); // Prevent row click
    navigate(`/company/${companyId}/proforma-invoices/${poId}/delivery-confirmation`);
  };

  const handleDeletePO = async (po, event) => {
    event.stopPropagation(); // Prevent row click
    setPoToDelete(po);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!poToDelete) return;

    try {
      const { error } = await supabase
        .from('customer_purchase_orders')
        .delete()
        .eq('id', poToDelete.id);

      if (error) throw error;
      
      // Refresh the list
      fetchPurchaseOrders();
      setDeleteDialogOpen(false);
      setPoToDelete(null);
    } catch (err) {
      console.error('Error deleting purchase order:', err);
      setError('Failed to delete purchase order: ' + err.message);
      setDeleteDialogOpen(false);
      setPoToDelete(null);
    }
  };

  const cancelDelete = () => {
    setDeleteDialogOpen(false);
    setPoToDelete(null);
  };

  // Status chip styling (Processing Status)
  const getStatusChip = (status) => {
    const statusConfig = {
      draft: { color: 'default', label: 'Draft', icon: <EditIcon fontSize="small" /> },
      pending_approval: { color: 'warning', label: 'Pending Approval', icon: <ScheduleIcon fontSize="small" /> },
      approved: { color: 'success', label: 'Approved', icon: <CheckCircleIcon fontSize="small" /> },
      in_production: { color: 'info', label: 'In Production', icon: <ScheduleIcon fontSize="small" /> },
      shipped: { color: 'primary', label: 'Shipped', icon: <LocalShippingIcon fontSize="small" /> },
      delivered: { color: 'success', label: 'Delivered', icon: <CheckCircleIcon fontSize="small" /> },
      cancelled: { color: 'error', label: 'Cancelled', icon: <CancelIcon fontSize="small" /> }
    };

    const config = statusConfig[status] || statusConfig.draft;

    return (
      <Chip
        icon={config.icon}
        label={config.label}
        color={config.color}
        size="small"
        sx={{
          fontWeight: 500,
          fontSize: '0.75rem',
          height: 24,
          borderRadius: 2,
          '& .MuiChip-icon': {
            fontSize: 14
          }
        }}
      />
    );
  };

  // Delivery Status chip styling
  const getDeliveryStatusChip = (deliveryStatus) => {
    const statusConfig = {
      pending: { color: 'default', label: 'Pending' },
      in_transit: { color: 'info', label: 'In Transit' },
      delivered: { color: 'primary', label: 'Delivered' },
      confirmed: { color: 'success', label: 'Confirmed' },
      issues_reported: { color: 'error', label: 'Issues' }
    };

    const config = statusConfig[deliveryStatus] || statusConfig.pending;

    return (
      <Chip
        label={config.label}
        color={config.color}
        size="small"
        variant="outlined"
        sx={{
          fontWeight: 500,
          fontSize: '0.7rem',
          height: 22,
          borderRadius: 2
        }}
      />
    );
  };

  // Priority chip styling
  const getPriorityChip = (priority) => {
    const priorityConfig = {
      low: { color: 'success', label: 'Low' },
      medium: { color: 'info', label: 'Medium' },
      high: { color: 'warning', label: 'High' },
      urgent: { color: 'error', label: 'Urgent' }
    };

    const config = priorityConfig[priority] || priorityConfig.medium;

    return (
      <Chip
        label={config.label}
        color={config.color}
        size="small"
        variant="outlined"
        sx={{
          fontWeight: 500,
          fontSize: '0.7rem',
          height: 20,
          borderRadius: 1
        }}
      />
    );
  };

  // Format date without timezone issues
  // This prevents the date from being off by one day due to UTC conversion
  const formatDate = (dateString) => {
    if (!dateString) return '—';
    // If it's already a formatted date string, return it
    if (typeof dateString === 'string' && dateString.includes('/')) {
      return dateString;
    }
    // Parse YYYY-MM-DD format in local timezone (not UTC)
    if (typeof dateString === 'string' && dateString.includes('-')) {
      const parts = dateString.split('-');
      if (parts.length === 3) {
        const year = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10);
        const day = parseInt(parts[2], 10);
        // Validate the parsed values
        if (!isNaN(year) && !isNaN(month) && !isNaN(day)) {
          // Create date in local timezone to avoid UTC conversion issues
          const date = new Date(year, month - 1, day);
          const formattedMonth = String(date.getMonth() + 1).padStart(2, '0');
          const formattedDay = String(date.getDate()).padStart(2, '0');
          const formattedYear = date.getFullYear();
          return `${formattedMonth}/${formattedDay}/${formattedYear}`;
        }
      }
    }
    // Fallback: use Date but add noon to avoid timezone issues
    const date = new Date(dateString + 'T12:00:00');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  // Permission check
  if (!hasPermission('sales.purchase_orders.page.view')) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center',
          minHeight: '70vh',
          textAlign: 'center'
        }}>
          <Card sx={{
            p: 8,
            borderRadius: 3,
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'white',
            boxShadow: `0 12px 40px ${alpha(theme.palette.grey[900], 0.08)}`,
            maxWidth: 600,
            width: '100%',
            position: 'relative',
            overflow: 'hidden',
            cursor: 'default',
            transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
            '&:hover': {
              transform: 'translateY(-8px)',
              boxShadow: `0 20px 60px ${alpha(theme.palette.grey[900], 0.15)}`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 4,
              background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.grey[400]})`,
              opacity: 0.8,
              transition: 'opacity 0.3s ease'
            },
            '&:hover::before': {
              opacity: 1
            }
          }}>
            <Box sx={{ 
              p: 3, 
              borderRadius: 3, 
              background: `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
              mb: 6,
              display: 'inline-flex',
              boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`
            }}>
              <RequestQuoteIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
            </Box>
            <Typography variant="h3" sx={{ 
              mb: 3, 
              fontWeight: 500, 
              color: 'text.primary',
              fontSize: '2.5rem',
              letterSpacing: '-0.8px'
            }}>
              Access Restricted
            </Typography>
            <Typography variant="h6" sx={{ 
              color: 'text.secondary', 
              mb: 4,
              fontWeight: 500,
              fontSize: '1.2rem'
            }}>
              Orders Permissions Required
            </Typography>
            <Typography variant="body1" sx={{ 
              color: 'text.secondary',
              lineHeight: 1.7,
              mb: 6,
              fontSize: '1.05rem',
              maxWidth: 450,
              mx: 'auto'
            }}>
              You don't currently have the necessary permissions to access Orders. 
              Contact your system administrator to request access or verify your role permissions.
            </Typography>
            <Button
              variant="outlined"
              onClick={() => navigate(-1)}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500,
                px: 4,
                py: 1.5,
                fontSize: '1rem'
              }}
            >
              Return to Previous Page
            </Button>
          </Card>
        </Box>
      </Container>
    );
  }

  // Check additional permissions
  const canCreate = hasPermission('sales.purchase_orders.create', 'write');
  const canDelete = hasPermission('sales.purchase_orders.delete', 'delete');
  const hasAnyDeliveryConfirmationPermission = 
    canViewDeliveryDocuments() || 
    canViewInventoryUsage() || 
    canViewInvoicePayment() || 
    canViewDeliveryDetails() || 
    canViewFinalApproval();

  if (loading) {
    return (
      <Container disableGutters maxWidth="xl" sx={{ px: 3, py: 4, pb: 16 }}>
        <Card sx={{ 
          borderRadius: 3,
          mt: 10,
          mx: 'auto',
          maxWidth: 600,
          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`,
          boxShadow: `0 12px 32px ${alpha('#2196F3', 0.15)}`,
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: `0 16px 40px ${alpha('#2196F3', 0.2)}`
          }
        }}>
          <CardContent sx={{ p: 6, textAlign: 'center' }}>
            <Box sx={{ mb: 3 }}>
              <Box sx={{
                display: 'inline-flex',
                p: 3,
                borderRadius: '50%',
                bgcolor: alpha('#2196F3', 0.1),
                mb: 2
              }}>
                <RequestQuoteIcon sx={{ fontSize: 48, color: '#2196F3' }} />
              </Box>
            </Box>
            <Typography variant="h6" sx={{
              fontWeight: 500,
              color: '#2196F3',
              mb: 1,
              fontSize: '1.1rem'
            }}>
              Loading Orders
            </Typography>
            <Typography variant="body2" sx={{ 
              color: 'text.secondary',
              fontWeight: 500,
              maxWidth: 400,
              mx: 'auto',
              lineHeight: 1.6,
              mb: 3
            }}>
              Retrieving your order data...
            </Typography>
            <Box sx={{ 
              width: '60%', 
              mx: 'auto',
              '& .MuiLinearProgress-root': {
                height: 8,
                borderRadius: 4,
                bgcolor: alpha(theme.palette.grey[300], 0.3),
                '& .MuiLinearProgress-bar': {
                  bgcolor: '#2196F3',
                  borderRadius: 4
                }
              }
            }}>
              <LinearProgress />
            </Box>
          </CardContent>
        </Card>
      </Container>
    );
  }

  return (
    <Fade in timeout={800}>
      <Box>
        <Container maxWidth="xl" sx={{ py: 3 }}>
          {/* Enhanced Page Header */}
          <Card sx={{
            borderRadius: 3,
            mt: 5,
            mb: 3,
            border: `1px solid ${theme.palette.grey[400]}`,
            background: theme.palette.grey[100],
            backdropFilter: 'blur(20px)',
            boxShadow: 'none',
            borderTop: '3px solid #2196F3',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: `0 6px 20px ${alpha('#2196F3', 0.15)}`,
            },
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, mb: 2 }}>
                <Box sx={{
                  p: 2,
                  borderRadius: 3,
                  background: alpha('#2196F3', 0.08),
                  border: `1px solid ${alpha('#2196F3', 0.15)}`,
                }}>
                  <RequestQuoteIcon sx={{ fontSize: 32, color: '#2196F3' }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h4" sx={{
                    fontWeight: 500,
                    color: '#2196F3',
                    mb: 1,
                    fontSize: '2rem',
                    letterSpacing: '-0.5px'
                  }}>
                    Customer Orders
                  </Typography>
                  <Typography variant="subtitle1" sx={{ 
                    color: 'text.secondary', 
                    fontWeight: 500,
                    fontSize: '1rem'
                  }}>
                    {companyName}
                  </Typography>
                </Box>
              </Box>
              <Typography variant="body2" sx={{ 
                color: 'text.secondary', 
                maxWidth: 700, 
                lineHeight: 1.6,
                fontSize: '0.95rem',
                fontWeight: 500
              }}>
                Manage proforma invoices from creation through delivery. Track invoices, monitor status, 
                and streamline your sales process.
              </Typography>
            </CardContent>
          </Card>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Filters Card */}
          <Card sx={{
            mb: 2,
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            borderRadius: 3,
            background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`,
            boxShadow: `0 8px 24px ${alpha(theme.palette.grey[500], 0.12)}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.25)}`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
            }
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
                <TextField
                  placeholder="Search by PI# or customer"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  size="small"
                  sx={{ 
                    minWidth: 300,
                    mt: 0.5,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 3,
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                      background: 'white',
                      '& fieldset': { border: 'none' },
                      '&:hover': {
                        boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                      },
                      '&.Mui-focused': {
                        boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.25)}`
                      }
                    }
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: theme.palette.primary.main }} />
                      </InputAdornment>
                    ),
                    sx: {
                      fontSize: '0.9rem',
                      fontWeight: 500
                    }
                  }}
                />
                
                <FormControl size="small" sx={{ minWidth: 180 }}>
                  <InputLabel sx={{ 
                    fontSize: '0.85rem', 
                    fontWeight: 500,
                    mt: 0.5
                  }}>
                    Filter by Status
                  </InputLabel>
                  <Select
                    value={filterStatus}
                    label="Filter by Status"
                    onChange={(e) => setFilterStatus(e.target.value)}
                    sx={{
                      borderRadius: 3,
                      mt: 0.5,
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                      background: 'white',
                      '& .MuiSelect-select': {
                        fontSize: '0.9rem',
                        fontWeight: 500
                      },
                      '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                      '&:hover': {
                        boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                      }
                    }}
                  >
                    <MenuItem value="all">All Statuses</MenuItem>
                    <MenuItem value="draft">Draft</MenuItem>
                    <MenuItem value="pending_approval">Pending Approval</MenuItem>
                    <MenuItem value="approved">Approved</MenuItem>
                    <MenuItem value="in_production">In Production</MenuItem>
                    <MenuItem value="shipped">Shipped</MenuItem>
                    <MenuItem value="delivered">Delivered</MenuItem>
                    <MenuItem value="cancelled">Cancelled</MenuItem>
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 160 }}>
                  <InputLabel sx={{ 
                    fontSize: '0.85rem', 
                    fontWeight: 500,
                    mt: 0.5
                  }}>
                    Priority
                  </InputLabel>
                  <Select
                    value={filterPriority}
                    label="Priority"
                    onChange={(e) => setFilterPriority(e.target.value)}
                    sx={{
                      borderRadius: 3,
                      mt: 0.5,
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                      background: 'white',
                      '& .MuiSelect-select': {
                        fontSize: '0.9rem',
                        fontWeight: 500
                      },
                      '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                      '&:hover': {
                        boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                      }
                    }}
                  >
                    <MenuItem value="all">All Priorities</MenuItem>
                    <MenuItem value="low">Low</MenuItem>
                    <MenuItem value="medium">Medium</MenuItem>
                    <MenuItem value="high">High</MenuItem>
                    <MenuItem value="urgent">Urgent</MenuItem>
                  </Select>
                </FormControl>
                
                <Box sx={{ flex: 1 }} />
                
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                  {filteredPOs.length} proforma invoice{filteredPOs.length !== 1 ? 's' : ''}
                </Typography>
              </Box>
            </CardContent>
          </Card>

          {/* Table or Empty State */}
          {filteredPOs.length === 0 ? (
            <Paper 
              sx={{
                textAlign: 'center',
                py: 8,
                px: 4,
                border: '2px dashed',
                borderColor: 'divider',
                borderRadius: 2,
              }}
            >
              <Box sx={{ 
                p: 2.5, 
                borderRadius: 3, 
                background: `linear-gradient(${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                backdropFilter: 'blur(10px)',
                boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.3)}`,
                mb: 3,
                width: 'fit-content',
                mx: 'auto'
              }}>
                <RequestQuoteIcon sx={{ fontSize: 36, color: 'text.secondary' }} />
              </Box>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 500 }}>
                {purchaseOrders.length === 0 ? 'No Proforma Invoices Yet' : 'No Matching Proforma Invoices'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 400, mx: 'auto', fontWeight: 500 }}>
              {purchaseOrders.length === 0 
                  ? 'Create your first proforma invoice to start tracking sales and deliveries.'
                  : 'Try adjusting your search or filter criteria.'}
          </Typography>
          {purchaseOrders.length === 0 && canCreate && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateNew}
                  sx={{ 
                    borderRadius: 3,
                    px: 4,
                    py: 1.5,
                    fontWeight: 500,
                    fontSize: '0.9rem',
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                    boxShadow: `0 6px 18px ${alpha(theme.palette.primary.main, 0.25)}`,
                    transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                    '&:hover': {
                      transform: 'translateY(-1px) scale(1.02)',
                      boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.35)}`
                    }
                  }}
                >
                  Create Your First Proforma Invoice
                </Button>
              )}
            </Paper>
          ) : (
            <Paper sx={{ 
              borderRadius: 3, 
              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
              background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`,
              boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
              overflow: 'hidden',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.25)}`,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
              }
            }}>
              <TableContainer>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ 
                        fontWeight: 500,
                        fontSize: '0.9rem',
                        bgcolor: 'white',
                        borderBottom: `2px solid ${theme.palette.primary.main}`,
                        color: 'primary.main',
                        py: 1.5,
                        px: 2.5
                      }}>
                        Proforma Invoice Number
                      </TableCell>
                      <TableCell sx={{ 
                        fontWeight: 500,
                        fontSize: '0.9rem',
                        bgcolor: 'white',
                        borderBottom: `2px solid ${theme.palette.primary.main}`,
                        color: 'primary.main',
                        py: 1.5,
                        px: 2.5
                      }}>
                        Customer
                      </TableCell>
                      <TableCell sx={{ 
                        fontWeight: 500,
                        fontSize: '0.9rem',
                        bgcolor: 'white',
                        borderBottom: `2px solid ${theme.palette.primary.main}`,
                        color: 'primary.main',
                        py: 1.5,
                        px: 2.5
                      }}>
                        Order Date
                      </TableCell>
                      <TableCell sx={{ 
                        fontWeight: 500,
                        fontSize: '0.9rem',
                        bgcolor: 'white',
                        borderBottom: `2px solid ${theme.palette.primary.main}`,
                        color: 'primary.main',
                        py: 1.5,
                        px: 2.5
                      }}>
                        Delivery Date
                      </TableCell>
                      <TableCell sx={{ 
                        fontWeight: 500,
                        fontSize: '0.9rem',
                        bgcolor: 'white',
                        borderBottom: `2px solid ${theme.palette.primary.main}`,
                        color: 'primary.main',
                        py: 1.5,
                        px: 2.5
                      }}>
                        Priority
                      </TableCell>
                      <TableCell sx={{ 
                        fontWeight: 500,
                        fontSize: '0.9rem',
                        bgcolor: 'white',
                        borderBottom: `2px solid ${theme.palette.primary.main}`,
                        color: 'primary.main',
                        py: 1.5,
                        px: 2.5
                      }}>
                        Processing Status
                      </TableCell>
                      <TableCell sx={{ 
                        fontWeight: 500,
                        fontSize: '0.9rem',
                        bgcolor: 'white',
                        borderBottom: `2px solid ${theme.palette.primary.main}`,
                        color: 'primary.main',
                        py: 1.5,
                        px: 2.5
                      }}>
                        Delivery Status
                      </TableCell>
                      <TableCell align="right" sx={{ 
                        fontWeight: 500,
                        fontSize: '0.9rem',
                        bgcolor: 'white',
                        borderBottom: `2px solid ${theme.palette.primary.main}`,
                        color: 'primary.main',
                        py: 1.5,
                        px: 2.5
                      }}>
                        Total Amount
                      </TableCell>
                      <TableCell align="center" sx={{ 
                        fontWeight: 500,
                        fontSize: '0.9rem',
                        bgcolor: 'white',
                        borderBottom: `2px solid ${theme.palette.primary.main}`,
                        color: 'primary.main',
                        py: 1.5,
                        px: 2.5,
                        width: 120
                      }}>
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(rowsPerPage > 0
                      ? filteredPOs.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      : filteredPOs
                    ).map((po) => (
                      <TableRow 
                        key={po.id} 
                        hover
                        onClick={() => handleViewPO(po.id)}
                        sx={{ 
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          '&:hover': { 
                            bgcolor: alpha(theme.palette.primary.main, 0.08),
                            transition: 'all 0.2s ease'
                          },
                          '& .MuiTableCell-root': {
                            borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`,
                            py: 2,
                            px: 2.5
                          }
                        }}
                      >
                        <TableCell>
                          <Typography variant="body1" sx={{ 
                            fontWeight: 500,
                            color: 'primary.main',
                            fontSize: '0.95rem'
                          }}>
                            {po.po_number}
                          </Typography>
                        </TableCell>
                        
                        <TableCell>
                          <Typography variant="body2" sx={{ 
                            fontWeight: 500,
                            fontSize: '0.9rem'
                          }}>
                            {po.customer_name || po.customer?.name || 'Unknown'}
                          </Typography>
                          {(po.customer_company || po.customer?.company) && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              {po.customer_company || po.customer?.company}
                            </Typography>
                          )}
                        </TableCell>
                        
                        <TableCell>
                          <Typography variant="body2" sx={{ fontSize: '0.9rem', fontWeight: 500 }}>
                            {formatDate(po.order_date)}
                          </Typography>
                        </TableCell>
                        
                        <TableCell>
                          <Typography variant="body2" sx={{ fontSize: '0.9rem', fontWeight: 500 }}>
                            {formatDate(po.actual_delivery_date || po.requested_delivery_date)}
                          </Typography>
                        </TableCell>
                        
                        <TableCell>
                          {getPriorityChip(po.priority_level)}
                        </TableCell>
                        
                        <TableCell>
                          {getStatusChip(po.status)}
                        </TableCell>
                        
                        <TableCell>
                          {getDeliveryStatusChip(po.delivery_status)}
                        </TableCell>
                        
                        <TableCell align="right">
                          <Typography variant="body1" sx={{ 
                            fontWeight: 500,
                            color: 'success.main',
                            fontSize: '0.95rem'
                          }}>
                            {formatCurrency(po.latest_po_creation?.amount_due || po.latest_po_creation?.total_amount || po.total_amount || 0)}
                          </Typography>
                        </TableCell>
                        
                        <TableCell align="center">
                          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                            {po.status === 'approved' && (
                              <Tooltip title={hasAnyDeliveryConfirmationPermission ? "Confirm Delivery" : "No Permission"}>
                                <span>
                                  <IconButton 
                                    size="small" 
                                    onClick={(e) => hasAnyDeliveryConfirmationPermission && handleConfirmDelivery(po.id, e)}
                                    disabled={!hasAnyDeliveryConfirmationPermission}
                                    sx={{
                                      color: 'success.main',
                                      border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
                                      borderRadius: 2,
                                      opacity: hasAnyDeliveryConfirmationPermission ? 1 : 0.3,
                                      cursor: hasAnyDeliveryConfirmationPermission ? 'pointer' : 'not-allowed',
                                      transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                                      '&:hover': hasAnyDeliveryConfirmationPermission ? {
                                        bgcolor: 'success.main',
                                        color: 'white',
                                        transform: 'scale(1.05)',
                                        boxShadow: `0 4px 12px ${alpha(theme.palette.success.main, 0.3)}`
                                      } : {},
                                      '&.Mui-disabled': {
                                        opacity: 0.3,
                                        color: 'success.main'
                                      }
                                    }}
                                  >
                                    <LocalShippingFilledIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}
                            <Tooltip title={canDelete ? "Delete" : "No Permission"}>
                              <span>
                                <IconButton 
                                  size="small" 
                                  onClick={(e) => canDelete && handleDeletePO(po, e)}
                                  disabled={!canDelete}
                                  sx={{
                                    color: 'error.main',
                                    border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
                                    borderRadius: 2,
                                    opacity: canDelete ? 1 : 0.3,
                                    cursor: canDelete ? 'pointer' : 'not-allowed',
                                    transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                                    '&:hover': canDelete ? {
                                      bgcolor: 'error.main',
                                      color: 'white',
                                      transform: 'scale(1.05)',
                                      boxShadow: `0 4px 12px ${alpha(theme.palette.error.main, 0.3)}`
                                    } : {},
                                    '&.Mui-disabled': {
                                      opacity: 0.3,
                                      color: 'error.main'
                                    }
                                  }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              
              <Box sx={{ 
                borderTop: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
                borderRadius: '0 0 12px 12px'
              }}>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  component="div"
                  count={filteredPOs.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  sx={{ 
                    px: 3,
                    py: 2,
                    '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                      fontSize: '0.9rem',
                      fontWeight: 500,
                      color: theme.palette.text.primary
                    },
                    '& .MuiTablePagination-select': {
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                      borderRadius: 2,
                      px: 2,
                      py: 1,
                      fontWeight: 500,
                      fontSize: '0.9rem',
                      background: 'white'
                    }
                  }}
                />
              </Box>
            </Paper>
          )}

          {/* Floating Add Button */}
          {canCreate && (
            <Fab
              color="primary"
              aria-label="add"
              sx={{
                position: 'fixed',
                bottom: 24,
                right: 24,
                width: 100,
                height: 100,
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                boxShadow: `0 6px 18px ${alpha(theme.palette.primary.main, 0.25)}`,
                transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                '&:hover': {
                  transform: 'translateY(-1px) scale(1.02)',
                  boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.35)}`
                }
              }}
              onClick={handleCreateNew}
            >
              <AddIcon sx={{ fontSize: 32 }} />
            </Fab>
          )}

          {/* Delete Confirmation Dialog */}
          <Dialog
            open={deleteDialogOpen}
            onClose={cancelDelete}
            maxWidth="sm"
            fullWidth
            PaperProps={{
              sx: {
                borderRadius: 3,
                border: 'none',
                background: `linear-gradient(135deg, ${alpha(theme.palette.grey[50], 0.9)}, ${alpha(theme.palette.grey[100], 0.9)})`,
                backdropFilter: 'blur(20px)',
                overflow: 'hidden'
              }
            }}
          >
            <DialogTitle sx={{ 
              p: 4, 
              borderBottom: `1px solid ${alpha(theme.palette.error.main, 0.1)}`,
              background: `linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.08)}, ${alpha(theme.palette.error.main, 0.04)})`,
              color: theme.palette.text.primary,
              fontWeight: 500,
              display: 'flex',
              alignItems: 'center',
              gap: 2
            }}>
              <WarningIcon sx={{ color: 'error.main', fontSize: 28 }} />
              Delete Proforma Invoice?
            </DialogTitle>
            <DialogContent sx={{ p: 4 }}>
              <Typography variant="body1" sx={{ mb: 3, mt: 3, fontWeight: 500 }}>
                Are you sure you want to delete this proforma invoice?
              </Typography>
              {poToDelete && (
                <Paper sx={{ p: 3, bgcolor: alpha(theme.palette.error.main, 0.05), border: `1px solid ${alpha(theme.palette.error.main, 0.2)}` }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>PI Number:</strong> {poToDelete.po_number}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Customer:</strong> {poToDelete.customer_name || 'Unknown'}
                  </Typography>
                  {(poToDelete.po_creation?.[0]?.amount_due || poToDelete.total_amount) > 0 && (
                    <Typography variant="body2">
                      <strong>Amount:</strong> {formatCurrency(poToDelete.po_creation?.[0]?.amount_due || poToDelete.total_amount || 0)}
                    </Typography>
                  )}
                </Paper>
              )}
              <Alert severity="warning" sx={{ mt: 3 }}>
                This action cannot be undone. All related data will be permanently deleted.
              </Alert>
            </DialogContent>
            <DialogActions sx={{ 
              p: 4, 
              gap: 2, 
              borderTop: `1px solid ${alpha(theme.palette.error.main, 0.1)}`,
              background: `linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.08)}, ${alpha(theme.palette.error.main, 0.04)})`
            }}>
              <Button 
                onClick={cancelDelete}
                sx={{ 
                  borderRadius: 2,
                  px: 3,
                  py: 1.5,
                  color: theme.palette.text.secondary,
                  fontWeight: 500
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={confirmDelete}
                sx={{
                  borderRadius: 2,
                  px: 4,
                  py: 1.5,
                  background: `linear-gradient(135deg, ${theme.palette.error.main}, ${theme.palette.error.dark})`,
                  color: 'white',
                  fontWeight: 500,
                  '&:hover': {
                    background: `linear-gradient(135deg, ${theme.palette.error.dark}, ${theme.palette.error.main})`
                  }
                }}
              >
                Delete Proforma Invoice
              </Button>
            </DialogActions>
          </Dialog>
        </Container>
      </Box>
    </Fade>
  );
};

export default ProformaInvoicesPage;